/**
 * `<rt-sidebar>`: slide-in navigation (old `Sidebar.tsx`). The element toggles its own
 * `data-open` attribute; styling is pure Tailwind on the server markup
 * (`group-data-open:` variants). Toggle buttons anywhere on the page reference it with
 * `data-sidebar-toggle="<sidebar id>"`.
 *
 * Closed panels are `inert` (replaces the old per-link `tabIndex` juggling). Escape and the
 * overlay close it; focus moves into the panel on open and back to the toggle on close.
 */
import { Island, defineElement } from '../lib/dom';

export class RtSidebar extends Island {
  #panel: HTMLElement | null = null;
  #returnFocus: HTMLElement | null = null;

  get open(): boolean {
    return this.hasAttribute('data-open');
  }

  protected start(signal: AbortSignal): void {
    this.#panel = this.querySelector<HTMLElement>('[data-sidebar-panel]');
    this.#apply(false);

    document.addEventListener(
      'click',
      (e) => {
        const toggle = (e.target as Element | null)?.closest<HTMLElement>('[data-sidebar-toggle]');
        if (toggle && toggle.dataset['sidebarToggle'] === this.id) {
          this.#returnFocus = toggle;
          this.toggle();
          return;
        }
        if (
          (e.target as Element | null)?.closest('[data-sidebar-overlay]') &&
          this.contains(e.target as Node)
        )
          this.toggle(false);
      },
      { signal },
    );
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.open) {
          this.toggle(false);
          this.#returnFocus?.focus();
        }
      },
      { signal },
    );
  }

  toggle(force?: boolean): void {
    this.#apply(force ?? !this.open);
  }

  #apply(open: boolean): void {
    this.toggleAttribute('data-open', open);
    if (this.#panel) this.#panel.inert = !open;
    for (const toggle of document.querySelectorAll<HTMLElement>('[data-sidebar-toggle]')) {
      if (toggle.dataset['sidebarToggle'] === this.id)
        toggle.setAttribute('aria-expanded', String(open));
    }
    if (open) this.#panel?.querySelector<HTMLElement>('a, button')?.focus({ preventScroll: true });
  }
}

defineElement('rt-sidebar', RtSidebar);

declare global {
  interface HTMLElementTagNameMap {
    'rt-sidebar': RtSidebar;
  }
}
